import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import { decrementPendingActionsCount } from "@/features/auth";
import type { LeaveRequestRevisionResponse } from "../types";
import { leavesApi } from "./leaves";
import { createLeavePendingRemovalPatches, leaveApprovalPredicate } from "./leavePendingPatches";

export const applicationApi = baseApi.injectEndpoints({
  endpoints(builder) {
    return {
      approveAnnualLeave: builder.mutation<
        { signing_document_id: string },
        { id: number; cms_signature: string; file_base64?: string }
      >({
        query({ id, cms_signature, file_base64 }) {
          return {
            url: `/api/hr/annual-leaves/${id}/approve/`,
            method: "POST",
            body: { cms_signature, file_base64 },
            credentials: "include",
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
            dispatch(
              leavesApi.util.updateQueryData("getAnnualLeave", id, (draft) => {
                draft.signing_status = 1;
                draft.status = "signing_pending";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      approveUnpaidLeave: builder.mutation<
        { signing_document_id: string },
        { id: number; cms_signature: string; file_base64?: string }
      >({
        query({ id, cms_signature, file_base64 }) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/approve/`,
            method: "POST",
            body: { cms_signature, file_base64 },
            credentials: "include",
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
            dispatch(
              leavesApi.util.updateQueryData("getUnpaidLeave", id, (draft) => {
                draft.signing_status = 1;
                draft.status = "signing_pending";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      approveMedicalLeave: builder.mutation<
        { signing_document_id: string },
        { id: number; cms_signature: string; file_base64?: string }
      >({
        query({ id, cms_signature, file_base64 }) {
          return {
            url: `/api/hr/medical-leaves/${id}/approve/`,
            method: "POST",
            body: { cms_signature, file_base64 },
            credentials: "include",
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
            dispatch(
              leavesApi.util.updateQueryData("getMedicalLeave", id, (draft) => {
                draft.signing_status = 1;
                draft.status = "signing_pending";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      rejectAnnualLeave: builder.mutation<{ status: string; note: string }, { id: number; note?: string }>({
        query({ id, note }) {
          return {
            url: `/api/hr/annual-leaves/${id}/reject/`,
            method: "POST",
            body: note ? { note } : {},
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      rejectUnpaidLeave: builder.mutation<{ status: string; note: string }, { id: number; note?: string }>({
        query({ id, note }) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/reject/`,
            method: "POST",
            body: note ? { note } : {},
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      rejectMedicalLeave: builder.mutation<{ status: string; note: string }, { id: number; note?: string }>({
        query({ id, note }) {
          return {
            url: `/api/hr/medical-leaves/${id}/reject/`,
            method: "POST",
            body: note ? { note } : {},
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      previewAnnualApplication: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/annual-leaves/${id}/preview-application/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      previewUnpaidApplication: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/preview-application/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      previewMedicalApplication: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/medical-leaves/${id}/preview-application/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      revokeAnnualLeave: builder.mutation<void, number>({
        query(id) {
          return {
            url: `/api/hr/annual-leaves/${id}/revoke/`,
            method: "POST",
            credentials: "include" as const,
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
      }),
      revokeUnpaidLeave: builder.mutation<void, number>({
        query(id) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/revoke/`,
            method: "POST",
            credentials: "include" as const,
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
      }),
      revokeMedicalLeave: builder.mutation<void, number>({
        query(id) {
          return {
            url: `/api/hr/medical-leaves/${id}/revoke/`,
            method: "POST",
            credentials: "include" as const,
          };
        },
        invalidatesTags() {
          return [
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
      }),
      requestRevisionAnnualLeave: builder.mutation<LeaveRequestRevisionResponse, { id: number; note: string }>({
        query({ id, note }) {
          return {
            url: `/api/hr/annual-leaves/${id}/request-revision/`,
            method: "POST",
            body: { note },
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      requestRevisionUnpaidLeave: builder.mutation<LeaveRequestRevisionResponse, { id: number; note: string }>({
        query({ id, note }) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/request-revision/`,
            method: "POST",
            body: { note },
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      requestRevisionMedicalLeave: builder.mutation<LeaveRequestRevisionResponse, { id: number; note: string }>({
        query({ id, note }) {
          return {
            url: `/api/hr/medical-leaves/${id}/request-revision/`,
            method: "POST",
            body: { note },
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const { pendingListPatch, pendingCountPatch } = createLeavePendingRemovalPatches(
            dispatch,
            leaveApprovalPredicate(id),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
    };
  },
});

export const {
  useApproveAnnualLeaveMutation,
  useApproveUnpaidLeaveMutation,
  useApproveMedicalLeaveMutation,
  useRejectAnnualLeaveMutation,
  useRejectUnpaidLeaveMutation,
  useRejectMedicalLeaveMutation,
  usePreviewAnnualApplicationMutation,
  usePreviewUnpaidApplicationMutation,
  usePreviewMedicalApplicationMutation,
  useRevokeAnnualLeaveMutation,
  useRevokeUnpaidLeaveMutation,
  useRevokeMedicalLeaveMutation,
  useRequestRevisionAnnualLeaveMutation,
  useRequestRevisionUnpaidLeaveMutation,
  useRequestRevisionMedicalLeaveMutation,
} = applicationApi;
